import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, Form, FormGroup, Label, Input, Row, Col, Button } from 'reactstrap';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import 'react-datepicker/dist/react-datepicker.css';
import diagnosisOptions from '../../../pages/Chat/DiagnosisNote/Dataset/options';
import { toast } from 'react-toastify';
import { AdminService } from '../../../services/AdminService';

interface JotformData {
  clientName?: string;
  formName: string;
  resultLink: string;
  submissionDate: string;
  score_GAD_7: number;
  score_PHQ_9: number;
  score_AUDIT: number;
  score_DAST: number;
  therapistId?: string;
  claims?: number;
  client?: {
    _id: string;
    name: string;
    firstname: string;
    lastname: string;
    email: string;
    dateOfBirth?: string;
    providerName?: string;
    providerNPI?: string;
    memberId?: string;
    organizationName?: string;
    address1?: string;
    city?: string;
    gender?: string;
    state?: string;
    postalCode?: string;
    diag_1?: string | null;
    diag_2?: string | null;
  };
  clientId?: string;
}

interface FormSubmissionsTableProps {
  jotformData: JotformData[];
  isLoading: boolean;
}

const styles = {
  tableContainer: {
    background: '#ffffff',
    borderRadius: '0.5rem',
    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
    padding: '10px',
    border: '1px solid #e9ecef',
    overflow: 'hidden',
    marginTop: '2rem'
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0',
    fontSize: '0.875rem',
    color: '#1f2937'
  },
  tableHeader: {
    padding: '15px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#495057',
    backgroundColor: '#f8f9fa',
    borderBottom: '2px solid #dee2e6',
    textAlign: 'left'
  },
  tableRowEven: {
    backgroundColor: 'white',
    borderBottom: '1px solid #f3f4f6'
  },
  tableRowOdd: {
    backgroundColor: '#fafafa',
    borderBottom: '1px solid #f3f4f6'
  },
  tableCell: {
    padding: '15px',
    verticalAlign: 'middle'
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#374151',
    marginBottom: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '30px 0',
  },
  dividerLine: {
    flex: 1,
    height: '2px',
    background: 'linear-gradient(to right, transparent, #dee2e6)'
  },
  dividerLineRight: {
    flex: 1,
    height: '2px',
    background: 'linear-gradient(to left, transparent, #dee2e6)'
  },
  dividerText: {
    color: '#495057',
    fontWeight: 'bold',
    letterSpacing: '1px',
    fontSize: '14px',
    backgroundColor: '#f8f9fa',
    padding: '8px 16px',
    borderRadius: '20px',
    border: '1px solid #dee2e6',
    margin: '0 15px'
  },
  actionButton: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#f97316',
    padding: '5px 10px',
    borderRadius: '4px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  dropdownMenu: {
    position: 'absolute' as const,
    backgroundColor: 'white',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    borderRadius: '8px',
    overflow: 'hidden',
    minWidth: '150px',
    zIndex: 1000,
    marginTop: '5px',
    right: '0'
  },
  dropdownItem: {
    padding: '10px 15px',
    fontSize: '14px',
    color: '#333',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    whiteSpace: 'nowrap' as const,
    fontWeight: '500',
    display: 'block',
    width: '100%',
    textAlign: 'left' as const,
    border: 'none',
    backgroundColor: 'transparent'
  }
};

// Helper function to get score colors based on value ranges
const getScoreColor = (score: number, type: string) => {
  if (score === 0) return '#6c757d';
  
  if (type === 'PHQ_9') {
    if (score < 5) return '#28a745'; // Minimal
    if (score < 10) return '#ffc107'; // Mild
    if (score < 15) return '#fd7e14'; // Moderate
    if (score < 20) return '#dc3545'; // Moderately Severe
    return '#6f42c1'; // Severe
  }
  
  if (type === 'GAD_7') {
    if (score < 5) return '#28a745'; // Minimal
    if (score < 10) return '#ffc107'; // Mild
    if (score < 15) return '#fd7e14'; // Moderate
    return '#dc3545'; // Severe
  }
  
  if (type === 'AUDIT') {
    if (score < 8) return '#28a745'; // Low Risk
    if (score < 16) return '#ffc107'; // Medium Risk
    if (score < 20) return '#fd7e14'; // High Risk
    return '#dc3545'; // Very High Risk
  }
  
  if (type === 'DAST') {
    if (score < 3) return '#28a745'; // Low
    if (score < 6) return '#ffc107'; // Moderate
    return '#dc3545'; // Substantial
  }
  
  return '#6c757d'; // Default gray for unknown types
};

// Helper function to format date
const formatDate = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const FormSubmissionsTable: React.FC<FormSubmissionsTableProps> = ({ jotformData, isLoading }) => {
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [isClaimModalOpen, setIsClaimModalOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState<JotformData | null>(null);
  const [serviceDate, setServiceDate] = useState<Date | null>(new Date());
  const [chargeAmount, setChargeAmount] = useState<string>('0');
  const [diagnosisCode, setDiagnosisCode] = useState<any>(null);
  const [secondaryDiagnosisCode, setSecondaryDiagnosisCode] = useState<any>(null);
  const [procedureCode, setProcedureCode] = useState<string>('90791');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleDropdown = (index: number) => {
    if (activeDropdown === index) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(index);
    }
  };

  const handleSubmitClaims = (form: JotformData) => {
    setSelectedForm(form);
    setIsClaimModalOpen(true);
    
    // Thiết lập giá trị mặc định cho diagnosisCode và secondaryDiagnosisCode
    const diag1 = form?.client?.diag_1;
    console.log('diag1:', diag1);
    console.log('diagnosisOptions:', diagnosisOptions.slice(0, 5)); // Hiển thị 5 mục đầu tiên để debug
    
    if (diag1 && diag1 !== "") {
      // Check both formats: with and without dots
      const formattedDiag1 = diag1.length === 5 && diag1.indexOf('.') === -1 ? 
        diag1.substring(0, 3) + '.' + diag1.substring(3) : diag1;
      
      console.log('formattedDiag1:', formattedDiag1);
      
      // Search for both formats in diagnosisOptions
      let primaryDiagOption = diagnosisOptions.find(option => option.value === diag1);
      if (!primaryDiagOption) {
        primaryDiagOption = diagnosisOptions.find(option => option.value === formattedDiag1);
      }
      
      console.log('Found primaryDiagOption:', primaryDiagOption);
      
      if (primaryDiagOption) {
        setDiagnosisCode(primaryDiagOption);
      } else {
        // Display error when code not found
        console.warn(`Diagnosis code "${diag1}" not found in options list`);
        setDiagnosisCode(null);
      }
    } else {
      setDiagnosisCode(null);
    }

    const diag2 = form?.client?.diag_2;
    console.log('diag2:', diag2);
    
    if (diag2 && diag2 !== "") {
      // Similarly, check both formats
      const formattedDiag2 = diag2.length === 5 && diag2.indexOf('.') === -1 ? 
        diag2.substring(0, 3) + '.' + diag2.substring(3) : diag2;
      
      console.log('formattedDiag2:', formattedDiag2);
      
      let secondaryDiagOption = diagnosisOptions.find(option => option.value === diag2);
      if (!secondaryDiagOption) {
        secondaryDiagOption = diagnosisOptions.find(option => option.value === formattedDiag2);
      }
      
      console.log('Found secondaryDiagOption:', secondaryDiagOption);
      
      if (secondaryDiagOption) {
        setSecondaryDiagnosisCode(secondaryDiagOption);
      } else {
        console.warn(`Second diagnosis code "${diag2}" not found in options list`);
        setSecondaryDiagnosisCode(null);
      }
    } else {
      setSecondaryDiagnosisCode(null);
    }
    
    setActiveDropdown(null);
  };

  const toggleClaimModal = () => {
    setIsClaimModalOpen(!isClaimModalOpen);
  };

  const handleServiceDateChange = (date: Date | null) => {
    setServiceDate(date);
  };

  // Handler for when Submit Claim button is clicked
  const submitClaim = async () => {
    if (!selectedForm || !selectedForm.client || !serviceDate) return;

    setIsSubmitting(true);

    // Format service date to MM/DD/YYYY
    const formatServiceDate = (date: Date) => {
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    };

    try {
      const payload = {
        organizationName: selectedForm.client.organizationName || '',
        tradingPartnerServiceId: '',
        firstname: selectedForm.client.firstname || '',
        lastname: selectedForm.client.lastname || '',
        address1: selectedForm.client.address1 || '',
        city: selectedForm.client.city || '',
        dateOfBirth: selectedForm.client.dateOfBirth || '',
        gender: selectedForm.client.gender || '',
        state: selectedForm.client.state || '',
        postalCode: selectedForm.client.postalCode || '',
        memberId: selectedForm.client.memberId || '',
        placeOfService: '11',
        providerFirstname: 'lavni',
        providerLastname: 'Provider',
        providerNPI: selectedForm.client.providerNPI || '',
        taxonomyCode: '101YA0400X',
        chargeAmount: chargeAmount,
        procCode: '90791',
        proc_code_1: procedureCode,
        modifier: '95',
        diagnosisCode: diagnosisCode ? diagnosisCode.value : '',
        secondaryDiagnosisCode: secondaryDiagnosisCode ? secondaryDiagnosisCode.value : '',
        serviceDate: formatServiceDate(serviceDate),
        clientId: selectedForm.client._id || '',
        therapistId: selectedForm.therapistId || ''
      };

      const response = await AdminService.submitClaimForAdmin(payload);

      if (response.success) {
        toast.success('Claim submitted successfully', {
          position: toast.POSITION.TOP_RIGHT
        });
        toggleClaimModal();
      } else {
        toast.error(response.error || 'Failed to submit claim', {
          position: toast.POSITION.TOP_RIGHT
        });
      }
    } catch (error) {
      console.error('Error submitting claim:', error);
      toast.error('Error submitting claim. Please try again.', {
        position: toast.POSITION.TOP_RIGHT
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => {
      setActiveDropdown(null);
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <div style={{
      background: 'linear-gradient(145deg, #f8f9fa, #ffffff)',
      padding: '30px',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      border: '1px solid #e9ecef',
      marginTop: '30px'
    }}>
      {/* Title with icon */}
      <h4 style={{ 
        color: '#2c3e50', 
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '20px'
      }}>
        <span style={{
          background: '#4f46e5',
          color: 'white',
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <i className="bi bi-clipboard-data"></i>
        </span>
        Form Submissions
      </h4>

      {/* Forms Table */}
      <div style={{
        overflowX: 'auto',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
        padding: '10px',
        border: '1px solid #e9ecef',
        overflow: 'hidden'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'separate',
          borderSpacing: '0',
          fontSize: '0.875rem',
          color: '#1f2937'
        }}>
          <thead>
            <tr style={{
              background: '#f8f9fa',
              borderBottom: '2px solid #dee2e6',
            }}>
              <th style={{ padding: '15px', fontSize: '14px', fontWeight: '600', color: '#495057' }}>Client Name</th>
              <th style={{ padding: '15px', fontSize: '14px', fontWeight: '600', color: '#495057' }}>Form Name</th>
              <th style={{ padding: '15px', fontSize: '14px', fontWeight: '600', color: '#495057' }}>Submission Date</th>
              <th style={{ padding: '15px', fontSize: '14px', fontWeight: '600', color: '#495057', textAlign: 'center' }}>Score PHQ 9</th>
              <th style={{ padding: '15px', fontSize: '14px', fontWeight: '600', color: '#495057', textAlign: 'center' }}>Score GAD 7</th>
              <th style={{ padding: '15px', fontSize: '14px', fontWeight: '600', color: '#495057', textAlign: 'center' }}>Score AUDIT</th>
              <th style={{ padding: '15px', fontSize: '14px', fontWeight: '600', color: '#495057', textAlign: 'center' }}>Score DAST</th>
              <th style={{ padding: '15px', fontSize: '14px', fontWeight: '600', color: '#495057', textAlign: 'center' }}>Claims</th>
              <th style={{ padding: '15px', fontSize: '14px', fontWeight: '600', color: '#495057', textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} style={{ padding: '20px', textAlign: 'center' }}>Loading data...</td>
              </tr>
            ) : jotformData.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ padding: '20px', textAlign: 'center' }}>No form submissions found</td>
              </tr>
            ) : (
              jotformData.map((form, index) => (
                <tr key={index} style={{
                  transition: 'all 0.2s ease',
                  backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                  borderBottom: '1px solid #f3f4f6'
                }}>
                  <td style={{ padding: '15px', verticalAlign: 'middle', fontWeight: '500' }}>{form.client ? form.client.name : 'Unknown'}</td>
                  <td style={{ padding: '15px', verticalAlign: 'middle' }}>{form.formName}</td>
                  <td style={{ padding: '15px', verticalAlign: 'middle', color: '#6c757d' }}>{formatDate(form.submissionDate)}</td>
                  <td style={{ padding: '15px', verticalAlign: 'middle', textAlign: 'center' }}>
                    {form.score_PHQ_9 > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                        <span style={{ 
                          fontWeight: '600', 
                          fontSize: '18px', 
                          color: getScoreColor(form.score_PHQ_9, 'PHQ_9')
                        }}>
                          {form.score_PHQ_9}
                        </span>
                        {(form.formName === "PHQ-9 and GAD-7") && (
                          <span
                            onClick={() => window.open(form.resultLink, "_blank")}
                            style={{
                              color: '#ff6634',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600',
                              textDecoration: 'underline',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '3px'
                            }}
                          >
                            <i className="bi bi-eye-fill" style={{ fontSize: '10px' }}></i> View
                          </span>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: '#6c757d', fontSize: '16px' }}>0</span>
                    )}
                  </td>
                  <td style={{ padding: '15px', verticalAlign: 'middle', textAlign: 'center' }}>
                    {form.score_GAD_7 > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                        <span style={{ 
                          fontWeight: '600', 
                          fontSize: '18px', 
                          color: getScoreColor(form.score_GAD_7, 'GAD_7')
                        }}>
                          {form.score_GAD_7}
                        </span>
                        {(form.formName === "PHQ-9 and GAD-7") && (
                          <span
                            onClick={() => window.open(form.resultLink, "_blank")}
                            style={{
                              color: '#ff6634',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600',
                              textDecoration: 'underline',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '3px'
                            }}
                          >
                            <i className="bi bi-eye-fill" style={{ fontSize: '10px' }}></i> View
                          </span>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: '#6c757d', fontSize: '16px' }}>0</span>
                    )}
                  </td>
                  <td style={{ padding: '15px', verticalAlign: 'middle', textAlign: 'center' }}>
                    {form.score_AUDIT > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                        <span style={{ 
                          fontWeight: '600', 
                          fontSize: '18px', 
                          color: getScoreColor(form.score_AUDIT, 'AUDIT')
                        }}>
                          {form.score_AUDIT}
                        </span>
                        {form.formName === "AUDIT" && (
                          <span
                            onClick={() => window.open(form.resultLink, "_blank")}
                            style={{
                              color: '#ff6634',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600',
                              textDecoration: 'underline',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '3px'
                            }}
                          >
                            <i className="bi bi-eye-fill" style={{ fontSize: '10px' }}></i> View
                          </span>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: '#6c757d', fontSize: '16px' }}>0</span>
                    )}
                  </td>
                  <td style={{ padding: '15px', verticalAlign: 'middle', textAlign: 'center' }}>
                    {form.score_DAST > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                        <span style={{ 
                          fontWeight: '600', 
                          fontSize: '18px', 
                          color: getScoreColor(form.score_DAST, 'DAST')
                        }}>
                          {form.score_DAST}
                        </span>
                        {form.formName === "DAST" && (
                          <span
                            onClick={() => window.open(form.resultLink, "_blank")}
                            style={{
                              color: '#ff6634',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600',
                              textDecoration: 'underline',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '3px'
                            }}
                          >
                            <i className="bi bi-eye-fill" style={{ fontSize: '10px' }}></i> View
                          </span>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: '#6c757d', fontSize: '16px' }}>0</span>
                    )}
                  </td>
                  <td style={{ padding: '15px', verticalAlign: 'middle', textAlign: 'center' }}>
                    {(form.claims && form.claims > 0) ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                        <span style={{ 
                          fontWeight: '600', 
                          fontSize: '18px', 
                          color: '#4f46e5'
                        }}>
                          {form.claims}
                        </span>
                      </div>
                    ) : (
                      <span style={{ color: '#6c757d', fontSize: '16px' }}>0</span>
                    )}
                  </td>
                  <td style={{ padding: '15px', verticalAlign: 'middle', textAlign: 'center', position: 'relative' }}>
                    <div onClick={(e) => {
                      e.stopPropagation();
                      toggleDropdown(index);
                    }} style={{ display: 'inline-block' }}>
                      <button
                        style={styles.actionButton}
                        aria-label="Actions"
                      >
                        <span style={{ 
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '2px',
                          color: '#f97316'
                        }}>
                          <span style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: '#f97316' }}></span>
                          <span style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: '#f97316' }}></span>
                          <span style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: '#f97316' }}></span>
                        </span>
                      </button>
                      
                      {activeDropdown === index && (
                        <div style={{ 
                          position: 'absolute',
                          backgroundColor: 'white',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          minWidth: '150px',
                          zIndex: 1000,
                          marginTop: '5px',
                          right: '0'
                        }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSubmitClaims(form);
                            }}
                            style={{ 
                              padding: '10px 15px',
                              fontSize: '14px',
                              color: '#333',
                              cursor: 'pointer',
                              transition: 'background-color 0.2s',
                              whiteSpace: 'nowrap',
                              fontWeight: '500',
                              display: 'block',
                              width: '100%',
                              textAlign: 'left',
                              border: 'none',
                              backgroundColor: 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f3f4f6';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            Submit Claims
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Claims Submission Modal */}
      <Modal isOpen={isClaimModalOpen} toggle={toggleClaimModal} size="lg">
        <ModalHeader toggle={toggleClaimModal}>
          Claim Submission
        </ModalHeader>
        <ModalBody>
          {selectedForm && selectedForm.client ? (
            <Form>
              <h5 style={{ color: '#495057', marginBottom: '1rem', fontSize: '1.1rem', fontWeight: 'bold' }}>
                Subscriber Details
              </h5>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="memberId">Member Id</Label>
                    <Input
                      type="text"
                      id="memberId"
                      value={selectedForm.client.memberId || ''}
                      readOnly
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="firstname">Firstname</Label>
                    <Input
                      type="text"
                      id="firstname"
                      value={selectedForm.client.firstname || ''}
                      readOnly
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="lastname">Lastname</Label>
                    <Input
                      type="text"
                      id="lastname"
                      value={selectedForm.client.lastname || ''}
                      readOnly
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="dob">Date of Birth</Label>
                    <Input
                      type="text"
                      id="dob"
                      value={selectedForm.client.dateOfBirth || ''}
                      readOnly
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="gender">Gender</Label>
                    <Input
                      type="text"
                      id="gender"
                      value={selectedForm.client.gender || ''}
                      readOnly
                    />
                  </FormGroup>
                </Col>
                <Col md={12}>
                  <FormGroup>
                    <Label for="organizationName">Organization Name</Label>
                    <Input
                      type="text"
                      id="organizationName"
                      value={selectedForm.client.organizationName || ''}
                      readOnly
                    />
                  </FormGroup>
                </Col>
              </Row>

              <h5 style={{ color: '#495057', marginTop: '1.5rem', marginBottom: '1rem', fontSize: '1.1rem', fontWeight: 'bold' }}>
                Additional Information
              </h5>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="address">Address</Label>
                    <Input
                      type="text"
                      id="address"
                      value={selectedForm.client.address1 || ''}
                      readOnly
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="city">City</Label>
                    <Input
                      type="text"
                      id="city"
                      value={selectedForm.client.city || ''}
                      readOnly
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="state">State</Label>
                    <Input
                      type="text"
                      id="state"
                      value={selectedForm.client.state || ''}
                      readOnly
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="postalCode">Postal Code</Label>
                    <Input
                      type="text"
                      id="postalCode"
                      value={selectedForm.client.postalCode || ''}
                      readOnly
                    />
                  </FormGroup>
                </Col>
              </Row>

              <h5 style={{ color: '#495057', marginTop: '1.5rem', marginBottom: '1rem', fontSize: '1.1rem', fontWeight: 'bold' }}>
                Provider Details
              </h5>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="npi">NPI</Label>
                    <Input
                      type="text"
                      id="npi"
                      value={selectedForm.client.providerNPI || ''}
                      readOnly
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="providerName">Provider Name</Label>
                    <Input
                      type="text"
                      id="providerName"
                      value={selectedForm.client.providerName || ''}
                      readOnly
                    />
                  </FormGroup>
                </Col>
              </Row>

              <h5 style={{ color: '#495057', marginTop: '1.5rem', marginBottom: '1rem', fontSize: '1.1rem', fontWeight: 'bold' }}>
                Claim Details
              </h5>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="serviceDate">Service Date</Label>
                    <DatePicker
                      selected={serviceDate}
                      onChange={handleServiceDateChange}
                      dateFormat="MM/dd/yyyy"
                      className="form-control"
                      id="serviceDate"
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="chargeAmount">Charge Amount</Label>
                    <Input
                      type="number"
                      id="chargeAmount"
                      value={chargeAmount}
                      onChange={(e) => setChargeAmount(e.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col md={12}>
                  <FormGroup>
                    <Label for="diagnosisCode">Diagnosis Codes</Label>
                    <Select
                      options={diagnosisOptions}
                      value={diagnosisCode}
                      onChange={setDiagnosisCode}
                      placeholder="Select diagnosis code..."
                      isSearchable={true}
                      className="basic-single"
                      classNamePrefix="select"
                    />
                  </FormGroup>
                </Col>
                <Col md={12}>
                  <FormGroup>
                    <Label for="secondaryDiagnosisCode">Secondary Diagnosis Codes</Label>
                    <Select
                      options={diagnosisOptions}
                      value={secondaryDiagnosisCode}
                      onChange={setSecondaryDiagnosisCode}
                      placeholder="Select secondary diagnosis code..."
                      isSearchable={true}
                      className="basic-single"
                      classNamePrefix="select"
                    />
                  </FormGroup>
                </Col>
                <Col md={12}>
                  <FormGroup>
                    <Label for="procedureCode">Procedure Code</Label>
                    <Input
                      type="select"
                      id="procedureCode"
                      value={procedureCode}
                      onChange={(e) => setProcedureCode(e.target.value)}
                    >
                      <option value="90791">90791 - CCA</option>
                      <option value="90837">90837 - Individual Therapy 53min+</option>
                      <option value="90834">90834 - Individual Therapy 45min</option>
                      <option value="90832">90832 - Individual Therapy 30min</option>
                      <option value="90847">90847 - Couples Therapy WITH Client Present</option>
                      <option value="90847">90847 - Family Therapy WITH Client Present</option>
                      <option value="90846">90846 - Couples Therapy WITHOUT Client Present (Partner Alone)</option>
                      <option value="90846">90846 - Family Therapy WITHOUT Client Present</option>
                      <option value="90853">90853 - Group Therapy</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <Button 
                  color="secondary" 
                  onClick={toggleClaimModal}
                >
                  Cancel
                </Button>
                <Button 
                  color="primary" 
                  style={{ 
                    backgroundColor: '#f97316', 
                    borderColor: '#f97316' 
                  }}
                  onClick={submitClaim}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Claim'}
                </Button>
              </div>
            </Form>
          ) : (
            <p>No client data available</p>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default FormSubmissionsTable;
